import { createSlice } from '@reduxjs/toolkit';
import type {
  CreateStaffAccountInput,
  StaffAccountDto,
  StaffAccountWithInvitationDto,
  StaffAccountListDto,
} from '@icebreaker/shared/staff';
import { type ApiErrorBody } from '@icebreaker/shared/errors';
import { createAsyncActions } from '@icebreaker/shared/store';

/**
 * PERSONEL HESAPLARI (panel kimliği ayrımı · Karar 4c — ekran turu).
 *
 * Ayrı dilim, `admin.slice` ile aynı özellik klasöründe (dosya tavanı bölmesi,
 * `admin-stats.slice` emsali). Mutasyon sonrası liste SUNUCUDAN tazelenir
 * (`INVALIDATION_MAP` `staffAccounts/*`): ikinci bir yönetici aynı anda davet
 * ya da askı yapmış olabilir, satır yerinde güncellenmez.
 *
 * DAVET DURUMU LİSTEDE TAŞINMAZ — sunucu sözleşmesi (`StaffAccountDto`) yalnız
 * `passwordSet` döner; "davet bekliyor / süresi doldu" bilgisi yoktur. Ekran
 * bu yüzden UYDURMAZ (CLAUDE.md md. 9): satırda "şifre kurulmadı" yazar,
 * gönderilen davetin son kullanması yalnız o oturumdaki `lastNotice`ta durur.
 */
export const fetchStaffAccounts = createAsyncActions<void, StaffAccountListDto>(
  'staffAccounts/fetch',
);
export const inviteStaff = createAsyncActions<
  CreateStaffAccountInput,
  StaffAccountWithInvitationDto
>('staffAccounts/invite');
export const reinviteStaff = createAsyncActions<
  { staffAccountId: string },
  StaffAccountWithInvitationDto
>('staffAccounts/reinvite');
/** Başarı yükü istekle aynı: bildirim cümlesi adresi buradan alır. */
export const cancelStaffInvitation = createAsyncActions<
  { staffAccountId: string; email: string },
  { staffAccountId: string; email: string }
>('staffAccounts/cancelInvitation');
export const suspendStaff = createAsyncActions<
  { staffAccountId: string },
  { staffAccountId: string }
>('staffAccounts/suspend');
export const unsuspendStaff = createAsyncActions<
  { staffAccountId: string },
  { staffAccountId: string }
>('staffAccounts/unsuspend');

type Phase = 'idle' | 'loading' | 'succeeded' | 'failed';

/** Ekranda TEK SEFERLİK durum cümlesi (`role="status"`). */
export type StaffNotice =
  | { readonly kind: 'invited'; readonly email: string; readonly expiresAt: string }
  | { readonly kind: 'cancelled'; readonly email: string };

export interface StaffAccountsState {
  list: { items: StaffAccountDto[]; phase: Phase; error: ApiErrorBody | null };
  /** Davet formu — hatası alanın altına yazılır, satır eylemlerinden ayrı tutulur. */
  invite: { phase: Phase; error: ApiErrorBody | null };
  /** Satır eylemleri (yeniden davet · iptal · askı) ortak mutasyon durumu. */
  mutation: { phase: Phase; error: ApiErrorBody | null };
  lastNotice: StaffNotice | null;
}

const initialState: StaffAccountsState = {
  list: { items: [], phase: 'idle', error: null },
  invite: { phase: 'idle', error: null },
  mutation: { phase: 'idle', error: null },
  lastNotice: null,
};

const slice = createSlice({
  name: 'staffAccounts',
  initialState,
  reducers: {
    /** Davet katmanı açılıp kapanırken önceki denemenin hatası taşınmaz. */
    inviteFormReset(state) {
      state.invite = { phase: 'idle', error: null };
    },
    mutationReset(state) {
      state.mutation = { phase: 'idle', error: null };
    },
    noticeDismissed(state) {
      state.lastNotice = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStaffAccounts.request, (state) => {
        state.list.phase = 'loading';
        state.list.error = null;
      })
      .addCase(fetchStaffAccounts.success, (state, action) => {
        state.list.phase = 'succeeded';
        state.list.items = [...action.payload.accounts];
      })
      .addCase(fetchStaffAccounts.failure, (state, action) => {
        state.list.phase = 'failed';
        state.list.error = action.payload;
      })
      .addCase(inviteStaff.request, (state) => {
        state.invite = { phase: 'loading', error: null };
      })
      .addCase(inviteStaff.success, (state, action) => {
        state.invite.phase = 'succeeded';
        state.lastNotice = {
          kind: 'invited',
          email: action.payload.account.email,
          expiresAt: action.payload.invitation.expiresAt,
        };
      })
      .addCase(inviteStaff.failure, (state, action) => {
        state.invite = { phase: 'failed', error: action.payload };
      })
      .addCase(reinviteStaff.request, (state) => {
        state.mutation = { phase: 'loading', error: null };
      })
      .addCase(reinviteStaff.success, (state, action) => {
        state.mutation.phase = 'succeeded';
        state.lastNotice = {
          kind: 'invited',
          email: action.payload.account.email,
          expiresAt: action.payload.invitation.expiresAt,
        };
      })
      .addCase(reinviteStaff.failure, (state, action) => {
        state.mutation = { phase: 'failed', error: action.payload };
      })
      .addCase(cancelStaffInvitation.request, (state) => {
        state.mutation = { phase: 'loading', error: null };
      })
      .addCase(cancelStaffInvitation.success, (state, action) => {
        state.mutation.phase = 'succeeded';
        state.lastNotice = { kind: 'cancelled', email: action.payload.email };
      })
      .addCase(cancelStaffInvitation.failure, (state, action) => {
        state.mutation = { phase: 'failed', error: action.payload };
      })
      .addCase(suspendStaff.request, (state) => {
        state.mutation = { phase: 'loading', error: null };
      })
      .addCase(suspendStaff.success, (state) => {
        state.mutation.phase = 'succeeded';
      })
      .addCase(suspendStaff.failure, (state, action) => {
        state.mutation = { phase: 'failed', error: action.payload };
      })
      .addCase(unsuspendStaff.request, (state) => {
        state.mutation = { phase: 'loading', error: null };
      })
      .addCase(unsuspendStaff.success, (state) => {
        state.mutation.phase = 'succeeded';
      })
      .addCase(unsuspendStaff.failure, (state, action) => {
        state.mutation = { phase: 'failed', error: action.payload };
      });
  },
});

export const { inviteFormReset, mutationReset, noticeDismissed } = slice.actions;
export const staffAccountsReducer = slice.reducer;
